'use client';

import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { BottomSheet } from '../ui/BottomSheet';
import { Button } from '../ui/Button';
import { cn } from '../../utils/cn';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useConfirmDialogStore } from '../../stores/confirmDialogStore';
import {
  confirmAnswerId,
  confirmButtons,
  dismissAnswer,
  isRefusalButton,
  type ConfirmButton,
} from './confirmAnswer';
import translations from './ConfirmDialog.translations';

const EXIT_MS = 340;

const variantFor = (button: ConfirmButton) => {
  if (button.kind === 'destructive') return 'destructiveSecondary' as const;
  if (isRefusalButton(button)) return 'tertiary' as const;
  return 'primary' as const;
};

const fillFor = (variant: ReturnType<typeof variantFor>) =>
  variant === 'primary' ? undefined : 'bg-fill-tertiary';

export const ConfirmDialogHost: FC = () => {
  const { t } = useLocalTranslation(translations);
  const request = useConfirmDialogStore((s) => s.queue[0] ?? null);
  const primaryHost = useConfirmDialogStore((s) => s.hosts[0]);
  const drop = useConfirmDialogStore((s) => s.drop);
  const addHost = useConfirmDialogStore((s) => s.addHost);
  const removeHost = useConfirmDialogStore((s) => s.removeHost);

  const [token] = useState(() => Symbol('confirm-dialog-host'));
  const [answeredId, setAnsweredId] = useState<number | null>(null);

  const requestRef = useRef(request);
  requestRef.current = request;
  const answeredRef = useRef<number | null>(null);
  const exitTimerRef = useRef<number | null>(null);

  useEffect(() => {
    addHost(token);
    return () => removeHost(token);
  }, [token, addHost, removeHost]);

  const answer = useCallback(
    (buttonId: string | null) => {
      const current = requestRef.current;
      if (!current || answeredRef.current === current.id) return;
      answeredRef.current = current.id;
      setAnsweredId(current.id);
      current.resolve(buttonId);
      if (exitTimerRef.current !== null) window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = window.setTimeout(() => {
        exitTimerRef.current = null;
        drop(current.id);
      }, EXIT_MS);
    },
    [drop],
  );

  const dismiss = useCallback(() => {
    const current = requestRef.current;
    if (!current) return;
    answer(dismissAnswer(current.options));
  }, [answer]);

  useEffect(() => {
    if (primaryHost !== token) return;
    const onPopState = () => {
      const { queue, drop: dropRequest } = useConfirmDialogStore.getState();
      dismiss();
      for (const queued of queue.slice(1)) {
        queued.resolve(dismissAnswer(queued.options));
        dropRequest(queued.id);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [dismiss, primaryHost, token]);

  useEffect(
    () => () => {
      if (exitTimerRef.current !== null) window.clearTimeout(exitTimerRef.current);
      const current = requestRef.current;
      if (current && answeredRef.current !== current.id) {
        current.resolve(dismissAnswer(current.options));
      }
    },
    [],
  );

  if (primaryHost !== token || !request) return null;

  const { title, message } = request.options;
  const buttons = confirmButtons(request.options);

  return (
    <BottomSheet
      isOpen={answeredId !== request.id}
      onClose={dismiss}
      hideHeader
      heightClass="h-auto max-h-[85vh]"
      title={title ?? message}
      closeAriaLabel={t('close')}
    >
      <div
        data-testid="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        className="flex flex-col pt-14 pb-2"
      >
        {title ? (
          <h2 className="text-label-primary text-[19px] font-semibold leading-[24px] text-center text-balance">
            {title}
          </h2>
        ) : null}
        <p className="mt-2 text-label-secondary text-[15px] leading-[20px] text-center whitespace-pre-line">
          {message}
        </p>
        <div className="mt-5 flex flex-col gap-2">
          {buttons.map((button, index) => {
            const variant = variantFor(button);
            return (
            <Button
              key={`${button.kind}-${button.id ?? index}`}
              size="l"
              stretched
              className={cn('whitespace-normal', fillFor(variant))}
              variant={variant}
              onClick={() => answer(confirmAnswerId(button))}
            >
              {button.text ??
                t(button.kind === 'cancel' ? 'cancel' : button.kind === 'ok' ? 'ok' : 'close')}
            </Button>
            );
          })}
        </div>
      </div>
    </BottomSheet>
  );
};
